import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { fontFamily } from '../../common/typography/Typography';
import { ModifyMetadataContext } from '../../../context/ModifyMetadataContext';
import useLanguage from '../../../store/language';
import { scaleUpHorizontal } from '../../../styles/LayoutStyles';

import { getTextFromLanguagePack } from '../../../utils';
import { useUserJudge } from '../../../store/uploade';

const MENU_LIST = [
  {
    label_ko: '대시보드',
    label_en: 'Dashboard',
    lang_code: 'header_tab1_label_1',
    path: '/dashboard',
  },
  {
    label_ko: '업로드',
    label_en: 'Upload',
    lang_code: 'header_tab5_label_1',
    path: '/upload',
    children: [
      {
        label_ko: '제출 예정',
        label_en: 'Submission',
        lang_code: 'dashboard30_text_8',
        path: '/submit',
        isShow: true,
      },
      {
        label_ko: '검수 중',
        label_en: 'Under Review',
        lang_code: 'dashboard30_text_9',
        path: '/review',
        isShow: true,
      },
      {
        label_ko: '보류 중',
        label_en: 'Pending Approval',
        lang_code: 'dashboard30_text_10',
        path: '/pending',
        isShow: true,
      },
      {
        label_ko: '사용 허가서',
        label_en: 'Release',
        lang_code: 'dashboard30_text_11',
        path: '/release',
        isShow: true,
      },
    ],
  },
  {
    label_ko: '포트폴리오',
    label_en: 'Portfolio',
    lang_code: 'header_tab2_label_1',
    path: '/portfolio',
  },
  {
    label_ko: '수익',
    label_en: 'Earning',
    lang_code: 'header_tab3_label_1',
    path: '/earning',
    isChildrenHide: false,
    children: [
      {
        label_ko: '수익 요약',
        label_en: 'Overview',
        lang_code: 'dashboard30_text_4',
        path: '/earning',
      },
      {
        label_ko: '지급 내역',
        label_en: 'Payment History',
        lang_code: 'dashboard30_text_5',
        path: '/payout',
      },
    ],
  },
  {
    label_ko: '헬프센터',
    label_en: 'Help Center',
    lang_code: 'header_tab4_label_1',
    path: '/help',
    children: [
      {
        label_ko: 'FAQ',
        label_en: 'FAQ',
        path: '/faq',
        isShow: true,
      },
      {
        label_ko: '공지사항',
        label_en: 'Notice',
        path: '/notice',
        isShow: true,
      },
    ],
  },
];

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isModified, handleModalOpen } = useContext(ModifyMetadataContext);
  const { language } = useLanguage(
    state => ({
      language: state.language,
    }),
    shallow
  );

  const selected = useCallback(
    idx => {
      let buff = [MENU_LIST[idx].path];
      if (MENU_LIST[idx].children) {
        buff = buff.concat(MENU_LIST[idx].children.flatMap(c => c.path));
      }
      return buff.includes(location.pathname);
    },
    [location.pathname]
  );

  const onClickTab = item => {
    if (isModified) handleModalOpen(() => navigate(item.path));
    else navigate(item.path);
  };

  const { judge, setJudge } = useUserJudge();

  return (
    <Container role={'navigation'}>
      {MENU_LIST.map((item, i) => (
        <div key={`menu-${i}`}>
          <TabButton
            language={language}
            id={`tabMenu-${i}`}
            tabIndex={0}
            role={'menuitem'}
            onClick={() => {
              setJudge(
                'selected',
                judge.video == 'R' || judge.video == 'N' ? (judge.video == 'N' && judge.image == 'N' ? 'video' : 'image') : 'video'
              );
              onClickTab(item);
            }}
            selected={selected(i)}
            data-language={language}
          >
            {getTextFromLanguagePack(item.lang_code)}
            <SelectedEffect
              selected={selected(i)}
              // selected={location.pathname.startsWith(item.path)}
            />
          </TabButton>
        </div>
      ))}
    </Container>
  );
};

export default Menu;

const SelectedEffect = styled.span`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing(3)};
  left: 0;
  right: 0;
  height: 2px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  visibility: ${({ selected }) => (selected ? 'visible' : 'hidden')};
`;

const TabButton = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  cursor: pointer;
  border: 0;
  ${fontFamily};
  font-size: ${props => props.theme.fontSize[props['data-language'] === 'en' ? '14' : '16']};
  font-style: normal;
  font-weight: bold;
  line-height: 1;
  color: ${({ selected }) => (selected ? '#fff' : '#ddd')};
  white-space: nowrap;
  &:hover {
    color: #fff;
    & ${SelectedEffect} {
      animation: ${scaleUpHorizontal} 300ms cubic-bezier(0.4, 0, 0.2, 1) 0s 1 normal forwards;
    }
  }
`;

const Container = styled.nav`
  height: 100%;
  color: ${props => props.theme.palette.white};
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing(12)};
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;
